import { isUndefined } from 'lodash';
import { postRequest, getRequest, patchRequest } from 'src/utils/axios';
import logger from 'src/utils/logger';
import parse from 'src/utils/parse';

interface CreateSessionPayload {
  projectId: string;
  orgId: string;
  customerId: string;
}

interface SessionData {
  data?: any;
  sessionStatus: string;
  createdAt: string;
  id: string;
  orgId: string;
  customerId: string;
  projectId: string;
  supportUsers: any[];
  caseKey: string;
}

interface SessionResponse {
  statusCode: number;
  message: string;
  data: SessionData;
}

interface MessagesResponse {
  statusCode: number;
  message: string;
  data: any[];
}

class ChatApi {
  async createSession(payload: CreateSessionPayload): Promise<SessionResponse> {
    return new Promise((resolve, reject) => {
      const successFunction = (response) => {
        response = parse(response);
        if (!isUndefined(response?.data)) {
          resolve(response);
        }
      };
      const errorFunction = (error) => {
        logger(error, 'error');
        reject(error);
      };
      const customHeader = {
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
      };
      const data = {
        url: `${process.env.REACT_APP_CHAT}/chat-sessions`,
        payload,
        successFunction,
        errorFunction,
        customHeader,
      };
      postRequest(data);
    });
  }

  async submitFeedback(
    payload: CreateSessionPayload
  ): Promise<SessionResponse> {
    return new Promise((resolve, reject) => {
      const successFunction = (response) => {
        response = parse(response);
        if (!isUndefined(response?.data)) {
          resolve(response);
        }
      };
      const errorFunction = (error) => {
        logger(error, 'error');
        reject(error);
      };
      const customHeader = {
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
      };
      const data = {
        url: `${process.env.REACT_APP_CHAT}/feedback`,
        payload,
        successFunction,
        errorFunction,
        customHeader,
      };
      postRequest(data);
    });
  }

  async getMessagesBySessionId(
    sessionId: string,
    page: number = 0,
    pageSize: number = 50,
    sort: string = '1'
  ): Promise<MessagesResponse> {
    return new Promise((resolve, reject) => {
      const successFunction = (response) => {
        response = parse(response);
        if (!isUndefined(response?.data)) {
          resolve(response.data);
        }
      };
      const errorFunction = (error) => {
        logger(error, 'error');
        reject(error);
      };
      const customHeader = {
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
      };
      const data = {
        url: `${process.env.REACT_APP_CHAT}/messages/${sessionId}?page=${page}&size=${pageSize}&sort=${sort}`,
        successFunction,
        errorFunction,
        customHeader,
      };
      getRequest(data);
    });
  }

  async getLastOpenSession(customerId: string): Promise<SessionResponse> {
    return new Promise((resolve, reject) => {
      const successFunction = (response) => {
        response = parse(response);
        if (!isUndefined(response?.data)) {
          resolve(response.data);
        }
      };
      const errorFunction = (error) => {
        logger(error, 'error');
        reject(error);
      };
      const customHeader = {
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
      };
      const data = {
        url: `${process.env.REACT_APP_CHAT}/chat-sessions/last-open-session/${customerId}`,
        successFunction,
        errorFunction,
        customHeader,
      };
      getRequest(data);
    });
  }

  async getSessionsByCustomerId(customerId: string): Promise<SessionResponse> {
    return new Promise((resolve, reject) => {
      const successFunction = (response) => {
        response = parse(response);
        if (!isUndefined(response?.data)) {
          resolve(response.data);
        }
      };
      const errorFunction = (error) => {
        logger(error, 'error');
        reject(error);
      };
      const customHeader = {
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
      };
      const data = {
        url: `${process.env.REACT_APP_CHAT}/chat-sessions?customerId=${customerId}`,
        successFunction,
        errorFunction,
        customHeader,
      };
      getRequest(data);
    });
  }

  async getNotifications(
    customerId: string,
    page: number = 0,
    pageSize: number = 10
  ): Promise<SessionResponse> {
    return new Promise((resolve, reject) => {
      const successFunction = (response) => {
        response = parse(response);
        if (!isUndefined(response?.data)) {
          resolve(response.data);
        }
      };
      const errorFunction = (error) => {
        logger(error, 'error');
        reject(error);
      };
      const customHeader = {
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
      };
      const data = {
        url: `${process.env.REACT_APP_CHAT}/notifications/getPaginated/${customerId}`,
        payload: {
          page,
          pageSize,
        },
        successFunction,
        errorFunction,
        customHeader,
      };
      getRequest(data);
    });
  }

  async getGlobalWorkingHours(): Promise<any> {
    return new Promise((resolve, reject) => {
      const successFunction = (response) => {
        response = parse(response);
        if (!isUndefined(response?.data)) {
          resolve(response.data);
        }
      };
      const errorFunction = (error) => {
        logger(error, 'error');
        reject(error);
      };
      const customHeader = {
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
      };
      const data = {
        url: `${process.env.REACT_APP_CHAT}/global-time`,
        successFunction,
        errorFunction,
        customHeader,
      };
      getRequest(data);
    });
  }

  async exportChat(sessionId: string): Promise<SessionResponse> {
    return new Promise((resolve, reject) => {
      const successFunction = (response) => {
        response = parse(response);
        if (!isUndefined(response?.data)) {
          resolve(response.data);
        }
      };
      const errorFunction = (error) => {
        logger(error, 'error');
        reject(error);
      };
      const customHeader = {
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
      };
      const data = {
        url: `${process.env.REACT_APP_CHAT}/messages/export/${sessionId}`,
        payload: {},
        successFunction,
        errorFunction,
        customHeader,
      };
      getRequest(data);
    });
  }

  async updateSessionStatus(
    sessionId: string,
    status: string
  ): Promise<SessionResponse> {
    return new Promise((resolve, reject) => {
      const successFunction = (response) => {
        response = parse(response);
        if (!isUndefined(response?.data)) {
          resolve(response);
        }
      };
      const errorFunction = (error) => {
        logger(error, 'error');
        reject(error);
      };
      const customHeader = {
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
      };
      const data = {
        url: `${process.env.REACT_APP_CHAT}/chat-sessions/${sessionId}?status=${status}`,
        payload: {},
        successFunction,
        errorFunction,
        customHeader,
      };
      patchRequest(data);
    });
  }

  async markAllAsRead(customerId: string): Promise<SessionResponse> {
    return new Promise((resolve, reject) => {
      const successFunction = (response) => {
        response = parse(response);
        if (!isUndefined(response?.data)) {
          resolve(response);
        }
      };
      const errorFunction = (error) => {
        logger(error, 'error');
        reject(error);
      };
      const customHeader = {
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
      };
      const data = {
        url: `${process.env.REACT_APP_CHAT}/notifications/seen/${customerId}`,
        payload: {},
        successFunction,
        errorFunction,
        customHeader,
      };
      patchRequest(data);
    });
  }
}

export const chatApi = new ChatApi();
